'use client';

import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';
import type { MouseEvent } from 'react';

export const CTA_POPUP_OPEN_EVENT = 'site:open-cta-popup';
export const GLOBAL_CTA_POPUP_DATA_ATTRIBUTE = 'data-cta-popup-links';

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);

export function coerceCtaPopupBoolean(value: unknown): boolean {
    if (typeof value === 'string') {
        return TRUE_VALUES.has(value.trim().toLowerCase());
    }
    return Boolean(value);
}

export function isHttpCtaUrl(value: unknown): boolean {
    return /^https?:\/\//i.test(String(value || '').trim());
}

function candidateDocuments(ownerDocument?: Document | null): Document[] {
    const documents: Document[] = [];
    const add = (document: Document | null | undefined) => {
        if (document && !documents.includes(document)) documents.push(document);
    };

    add(ownerDocument);
    if (typeof document !== 'undefined') add(document);

    if (typeof window !== 'undefined') {
        try {
            add(window.parent?.document);
        } catch {
            // Cross-origin parent access is intentionally ignored.
        }
    }

    return documents;
}

export function isGlobalCtaPopupEnabled(
    ownerDocument?: Document | null
): boolean {
    return candidateDocuments(ownerDocument).some((candidate) =>
        coerceCtaPopupBoolean(
            candidate.documentElement?.getAttribute(
                GLOBAL_CTA_POPUP_DATA_ATTRIBUTE
            )
        )
    );
}

export function setGlobalCtaPopupPreview(enabled: boolean): void {
    candidateDocuments().forEach((candidate) => {
        candidate.documentElement?.setAttribute(
            GLOBAL_CTA_POPUP_DATA_ATTRIBUTE,
            enabled ? 'true' : 'false'
        );
    });
}

export function requestCtaPopupOpen(
    href: string,
    sourceWindow?: Window | null
): boolean {
    const url = String(href || '').trim();
    if (!isHttpCtaUrl(url) || typeof window === 'undefined') return false;

    const originWindow = sourceWindow || window;
    const detail = { href: url };
    const OriginCustomEvent = (
        originWindow as unknown as { CustomEvent: typeof CustomEvent }
    ).CustomEvent;
    originWindow.dispatchEvent(
        new OriginCustomEvent(CTA_POPUP_OPEN_EVENT, { detail })
    );

    try {
        if (originWindow.parent && originWindow.parent !== originWindow) {
            const ParentCustomEvent = (
                originWindow.parent as unknown as {
                    CustomEvent: typeof CustomEvent;
                }
            ).CustomEvent;
            originWindow.parent.dispatchEvent(
                new ParentCustomEvent(CTA_POPUP_OPEN_EVENT, {
                    detail,
                })
            );
        }
    } catch {
        // Cross-origin parent access is intentionally ignored.
    }

    return true;
}

type CtaClickEvent = {
    preventDefault: () => void;
    stopPropagation: () => void;
    nativeEvent?: { stopImmediatePropagation?: () => void };
    currentTarget?: {
        ownerDocument?: Document | null;
    };
};

export function handleCtaPopupClick(
    event: CtaClickEvent,
    href: unknown,
    perCtaEnabled: unknown = false
): boolean {
    const url = String(href || '').trim();
    if (!isHttpCtaUrl(url)) return false;

    const ownerDocument = event.currentTarget?.ownerDocument;
    const enabled =
        coerceCtaPopupBoolean(perCtaEnabled) ||
        isGlobalCtaPopupEnabled(ownerDocument);
    if (!enabled) return false;

    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();

    return requestCtaPopupOpen(url, ownerDocument?.defaultView);
}

export function ctaLinkBehaviorProps(
    href: unknown,
    openInNewTab: unknown,
    openInPopup: unknown
): {
    onClickCapture: (event: MouseEvent<HTMLAnchorElement>) => void;
    target?: '_blank';
    rel?: string;
} {
    const newTabProps = buttonLinkTargetProps(
        coerceCtaPopupBoolean(openInNewTab) &&
            !coerceCtaPopupBoolean(openInPopup)
    );
    const newTabCapture = newTabProps.onClickCapture;

    return {
        ...newTabProps,
        onClickCapture: (event) => {
            if (handleCtaPopupClick(event, href, openInPopup)) return;
            newTabCapture?.(event);
        },
    };
}

export function niceJobCtaPopupProps(openInPopup: unknown): {
    onClickCapture: (event: MouseEvent<HTMLAnchorElement>) => void;
} {
    return {
        onClickCapture: (event) => {
            const href = event.currentTarget.getAttribute('href') || '';
            handleCtaPopupClick(event, href, openInPopup);
        },
    };
}
